/**
 * API error message utilities.
 * Extract user-facing messages from axios/API error responses for toast or UI.
 */

import axios from 'axios';

/** Shape of typical API error response (Laravel/axios style) */
interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      errors?: Record<string, string[]>;
    };
  };
}

/**
 * Get a single display message from an API/axios error.
 * Prefers response.data.errors (first field message, e.g. validation) so messages like
 * "ログインIDは既に存在します。" are shown; then response.data.message; then fallback.
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosError = err as ApiErrorResponse;
  const data = axiosError.response?.data;
  let message: string | undefined;

  const errors = data?.errors;
  if (errors && typeof errors === 'object') {
    const firstKey = Object.keys(errors)[0];
    const firstMessages = firstKey ? errors[firstKey] : undefined;
    if (Array.isArray(firstMessages) && firstMessages[0]) {
      message = firstMessages[0];
    }
  }
  if (!message) {
    message = data?.message;
  }

  if (message) {
    return message.replace(/\s*\(and\s+\d+\s+more\s+errors?\)\.?/g, '').trim();
  }

  return fallback;
}

/**
 * Same as {@link getApiErrorMessage}, but when the request used `responseType: 'blob'`,
 * axios leaves error `response.data` as a Blob (JSON must be read and parsed first).
 */
export async function getApiErrorMessageAsync(
  err: unknown,
  fallback: string,
): Promise<string> {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text) as {
          message?: string;
          errors?: Record<string, string[]>;
        };
        return getApiErrorMessage({ response: { data: parsed } }, fallback);
      } catch {
        return fallback;
      }
    }
  }
  return getApiErrorMessage(err, fallback);
}

/**
 * Get ALL validation error messages from an API/axios error.
 * Returns every message across all fields (flattened), preserving order.
 * Used when multiple toasts must be shown sequentially (e.g. BD §5.3 一括削除).
 */
export function getApiErrorMessages(err: unknown): string[] {
  const axiosError = err as ApiErrorResponse;
  const errors = axiosError.response?.data?.errors;
  if (errors && typeof errors === 'object') {
    return Object.values(errors).flat().filter(Boolean) as string[];
  }
  const message = axiosError.response?.data?.message;
  return message ? [message] : [];
}

/**
 * Check whether an error is an HTTP 404 (Not Found) response.
 * Used by global React Query caches to auto-refresh stale lists when an item
 * has been deleted in another tab/session.
 */
export function isNotFoundError(error: unknown): boolean {
  const err = error as { response?: { status?: number } };
  return err?.response?.status === 404;
}

/** Default message when fetch/list API fails (Japanese) */
export const DEFAULT_FETCH_ERROR_MESSAGE = 'データの取得に失敗しました。';

/** Default message when an update/save mutation fails */
export const DEFAULT_UPDATE_ERROR_MESSAGE = '更新ができませんでした。';

/** Default message when a mutation fails due to concurrent deletion or transient error */
export const DEFAULT_CONFLICT_ERROR_MESSAGE =
  'エラーが発生しました。しばらくしてから再度お試しください。';
